import type { CampoPropio, Cliente, NuevoCliente } from '../../../api/ops/clientes';
import { emailValido, instagramLimpio, instagramValido } from '../lista/textos';

/**
 * Lo que se escribe en el alta (2.2) y en «Datos» de la ficha (2.3), sin estilos: el formulario, lo que se comprueba en el
 * panel y el cuerpo de `createCustomer`.
 */
export interface DatosFormulario {
    nombre: string;
    telefono: string;
    email: string;
    instagram: string;
    idioma: string;
    nacimiento: string;
    consentimientoNacimiento: boolean;
    campos: Record<string, unknown>;
    etiquetas: string[];
    comercial: { whatsapp: boolean; email: boolean };
}

export type ErroresFormulario = Partial<Record<string, string>>;

export function formularioVacio(previo: Partial<DatosFormulario> = {}): DatosFormulario {
    return { nombre: '', telefono: '', email: '', instagram: '', idioma: 'es-ES', nacimiento: '', consentimientoNacimiento: false, campos: {}, etiquetas: [], comercial: { whatsapp: false, email: false }, ...previo };
}

/** Los datos de tu negocio de una ficha (no los de su cuenta). */
export function formularioDe(c: Cliente): DatosFormulario {
    return formularioVacio({
        nombre: c.local.display_name ?? '',
        telefono: c.local.phone ?? '',
        email: c.local.email ?? '',
        instagram: c.local.instagram_username ?? '',
        idioma: c.locale ?? 'es-ES',
        nacimiento: c.birth_date ?? '',
        consentimientoNacimiento: c.birth_date != null,
        campos: { ...(c.field_values ?? {}) },
        etiquetas: [...(c.tag_ids ?? [])],
    });
}

/** Comprueba en el panel lo que se puede saber sin el servidor (el resto lo dice la API con 409 o 422). */
export function validar(d: DatosFormulario, campos: readonly CampoPropio[], alta: boolean): ErroresFormulario {
    const e: ErroresFormulario = {};
    const tel = d.telefono.trim();
    const email = d.email.trim();
    const ig = instagramLimpio(d.instagram);
    if (alta && d.nombre.trim() === '' && tel === '' && email === '') {
        e.nombre = 'Escribe el nombre, el teléfono o el email.';
    }
    if (tel !== '' && tel.replace(/\D/g, '').length < 9) {
        e.telefono = 'Revisa el teléfono: faltan cifras.';
    }
    if (email !== '' && !emailValido(email)) {
        e.email = 'Escribe un email válido.';
    }
    if (ig !== '' && !instagramValido(ig)) {
        e.instagram = 'Escribe el usuario de Instagram: letras, números, puntos o guiones bajos.';
    }
    if (d.nacimiento !== '' && !d.consentimientoNacimiento) {
        e.consentimiento = 'Marca la casilla para guardar su fecha de nacimiento.';
    }
    for (const c of campos) {
        const v = d.campos[c.key];
        if (c.required && c.type !== 'boolean' && (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0))) {
            e[`campo:${c.key}`] = c.type === 'select' || c.type === 'multiselect' ? 'Elige una opción.' : 'Obligatorio.';
        }
    }
    if (alta && d.comercial.whatsapp && tel === '') {
        e.whatsapp = 'Para WhatsApp hace falta el teléfono.';
    }
    if (alta && d.comercial.email && email === '') {
        e.comercialEmail = 'Para el email hace falta el email.';
    }
    return e;
}

/** Valores de los campos propios para la API: número como número, vacíos fuera (o `null` al editar). */
export function valoresDeCampos(d: DatosFormulario, campos: readonly CampoPropio[], antes: Record<string, unknown> | null): Record<string, unknown> {
    const fuera: Record<string, unknown> = {};
    for (const c of campos) {
        const v = d.campos[c.key];
        const vacio = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
        const valor = vacio ? null : c.type === 'number' && typeof v === 'string' ? Number(v.replace(',', '.')) : v;
        if (antes === null) {
            if (!vacio || c.type === 'boolean') {
                fuera[c.key] = c.type === 'boolean' ? v === true : valor;
            }
        } else if (JSON.stringify(antes[c.key] ?? null) !== JSON.stringify(c.type === 'boolean' ? v === true : valor)) {
            fuera[c.key] = c.type === 'boolean' ? v === true : valor;
        }
    }
    return fuera;
}


/** Versión de los textos que ve el personal al marcar la información comercial (N2, `modulos/clientes.md` §12). */
export const VERSION_TEXTOS = '2026-10-21';

/** El cuerpo de `createCustomer` con lo escrito (G42: perfil local, sin consultar la Cuenta Reservaria). */
export function cuerpoAlta(d: DatosFormulario, campos: readonly CampoPropio[]): NuevoCliente {
    const cuerpo: NuevoCliente = { source: 'panel', locale: d.idioma };
    const nombre = d.nombre.trim();
    const telefono = d.telefono.trim();
    const email = d.email.trim();
    const ig = instagramLimpio(d.instagram);
    if (nombre !== '') {
        cuerpo.display_name = nombre;
    }
    if (telefono !== '') {
        cuerpo.phone = telefono.replace(/[\s().-]/g, '');
    }
    if (email !== '') {
        cuerpo.email = email.toLowerCase();
    }
    if (ig !== '') {
        cuerpo.instagram_username = ig;
    }
    if (d.etiquetas.length > 0) {
        cuerpo.tag_ids = d.etiquetas;
    }
    const valores = valoresDeCampos(d, campos, null);
    if (Object.keys(valores).length > 0) {
        cuerpo.field_values = valores;
    }
    if (d.nacimiento !== '') {
        cuerpo.birth_date = d.nacimiento;
        cuerpo.birth_date_consent = d.consentimientoNacimiento;
    }
    const comercial = (['whatsapp', 'email'] as const).filter((c) => d.comercial[c]);
    if (comercial.length > 0) {
        cuerpo.consents = comercial.map((channel) => ({ channel, purpose: 'marketing' as const, granted: true, notice_version: VERSION_TEXTOS }));
    }
    return cuerpo;
}

